import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updateCandidateEmail } from '../services/job.service';
import { jobKeys } from './useJobOffers';

export interface UseCandidateEmailParams {
  jobId: string;
}

/**
 * Centralized hook for updating candidate email.
 * Invalidates job candidates query so all UI (table, sidebar, dialogs) stays in sync.
 */
export function useCandidateEmail({ jobId }: UseCandidateEmailParams) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      candidateId,
      email,
    }: {
      candidateId: string;
      email: string;
    }) => {
      const token = await getAccessTokenSilently();
      const response = await updateCandidateEmail(token, candidateId, email);
      if (response.error) {
        throw new Error(response.error as string);
      }
      return response.data;
    },
    onSuccess: () => {
      if (jobId) {
        queryClient.invalidateQueries({ queryKey: jobKeys.candidatesForJob(jobId) });
      }
    },
  });

  return {
    updateEmail: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
}
